import { AfterViewInit, Directive, ElementRef, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock } from '../report-aggregation';
import { clearTextMeasureCache, withDeckMotion } from './chart-layout';
import type Highcharts from 'highcharts';

/**
 * A width change smaller than this is ignored. A block that grows taller can push the page over the
 * viewport, and the scrollbar that appears takes ~15px off the width -- which would rebuild the
 * block shorter, remove the scrollbar, and start again.
 */
const RESIZE_EPSILON = 8;

/**
 * What every chart block shares: the ReportBlock inputs, and a measurement of the width the plot
 * actually gets. Subclasses implement build(), re-run whenever the inputs or that width change.
 *
 * A @Directive rather than a plain class: Angular requires a decorator on any class declaring
 * @Input or lifecycle hooks that a component inherits.
 */
@Directive()
/**
 * Every chart here binds `[oneToOne]="true"`. highcharts-angular applies new options with
 * `chart.update(options, redraw, oneToOne)`, and left at its default of `false` that merges
 * collections by index and *keeps* anything the new options no longer mention -- so removing a
 * question from a block left its series drawn on the chart, stacked against a category that no
 * longer had a name and so labelled with its index.
 */
export abstract class ChartBlockBase implements ReportBlock, OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;

  protected readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private observer?: ResizeObserver;
  private measuredWidth = 0;
  private resolvedFont?: string;

  protected abstract build(): void;

  ngOnChanges() {
    this.build();
  }

  ngAfterViewInit() {
    // Observed rather than measured once: the deck can get narrower with no window resize --
    // opening the chat panel does exactly that.
    this.observer = new ResizeObserver(entries => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0);
      if (width > 0 && Math.abs(width - this.measuredWidth) >= RESIZE_EPSILON) {
        this.measuredWidth = width;
        this.buildForLayout();
      }
    });
    this.observer.observe(this.host.nativeElement);

    // A block whose height depends on measured text can be laid out before the page's webfont
    // arrives, and would then be sized against the fallback font for good -- build() only re-runs
    // on a width change, and a font swap is not one. Clearing is global and idempotent, so every
    // block doing it costs one rebuild each.
    document.fonts?.ready.then(() => {
      clearTextMeasureCache();
      this.resolvedFont = undefined;
      this.buildForLayout();
    }).catch(() => {
      // No font loading API, or it never settles: the first measurement stands.
    });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  /** Set while a rebuild only corrects the layout, so `deckMotion` lands it without animating. */
  private layoutPass = false;

  private buildForLayout() {
    this.layoutPass = true;
    try {
      this.build();
    } finally {
      this.layoutPass = false;
    }
  }

  /** The deck's chart motion, with no slide for a rebuild that only corrects the layout. */
  protected deckMotion(options: Highcharts.Options): Highcharts.Options {
    return withDeckMotion(options, !this.layoutPass);
  }

  /**
   * The width the plot actually gets, measured rather than assumed (see labelFitFor). The estimate
   * only stands in before anything has been laid out.
   *
   * Every block must set `:host { display: block; }` -- a custom element is inline by default, and
   * an inline element measures 0.
   */
  protected plotWidth(): number {
    // Highcharts' own left/right spacing, which the plot never gets.
    return Math.max(120, this.hostWidth() - 24);
  }

  /**
   * The block's own width, before any chart spacing. A block that draws sub-charts of its own --
   * small multiples -- pays each panel's spacing inside the panel and must not also pay the
   * single-chart spacing plotWidth() subtracts, or every panel comes out 24px narrower than it is.
   */
  protected hostWidth(): number {
    const estimate = this.compact ? 260 : this.block.segmentLocation === 'full' ? 860 : 420;
    return Math.max(120, this.measuredWidth || estimate);
  }

  protected labelSize(): number {
    // The prototype's `.row .lab` is 12px, and a compact panel takes one step down from it.
    return this.compact ? 11 : 12;
  }

  /**
   * The font a chart will actually render its labels in. Charts set `fontFamily: 'inherit'`, so
   * measuring against anything else measures the wrong thing. Read once per instance: it comes from
   * the page theme, which does not change under a block.
   */
  protected fontFamily(): string {
    if (this.resolvedFont === undefined) {
      let family = '';
      try {
        family = getComputedStyle(this.host.nativeElement).fontFamily;
      } catch {
        // No layout to read (a test environment); the measurer falls back to its estimate anyway.
      }
      this.resolvedFont = family || 'sans-serif';
    }
    return this.resolvedFont;
  }
}
